/**
 * Resource inspector: find running Elyra instances and their child
 * processes (language servers, headless browsers), with age, CPU time,
 * and memory - so "Elyra uses a lot of memory" reports turn into data.
 *
 * All parsing is pure and unit-tested; the only side effect (running `ps`)
 * lives in the CLI layer.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export interface ProcessRecord {
	pid: number;
	ppid: number;
	/** Elapsed wall-clock seconds since the process started. */
	elapsedSeconds: number;
	/** Accumulated CPU seconds. */
	cpuSeconds: number;
	/** Resident set size in KB. */
	rssKb: number;
	command: string;
}

export interface ElyraInstance {
	process: ProcessRecord;
	children: ProcessRecord[];
	/** RSS of the instance plus all children, in KB. */
	totalRssKb: number;
	warnings: string[];
}

// ── Thresholds ──────────────────────────────────────────────────────────────

const HIGH_MEMORY_KB = 500 * 1024;
const LONG_RUNNING_SECONDS = 24 * 60 * 60;

// ── Parsing ─────────────────────────────────────────────────────────────────

/** Parse `ps` etime format: [[dd-]hh:]mm:ss */
export function parseElapsed(value: string): number {
	const match = /^(?:(\d+)-)?(?:(\d+):)?(\d+):(\d+)$/.exec(value.trim());
	if (!match) return 0;
	const [, days, hours, minutes, seconds] = match;
	return Number(days ?? 0) * 86_400 + Number(hours ?? 0) * 3_600 + Number(minutes ?? 0) * 60 + Number(seconds ?? 0);
}

/** Parse `ps` cputime format: [[dd-]hh:]mm:ss(.fraction) */
export function parseCpuTime(value: string): number {
	const match = /^(?:(\d+)-)?(?:(\d+):)?(\d+):(\d+(?:\.\d+)?)$/.exec(value.trim());
	if (!match) return 0;
	const [, days, hours, minutes, seconds] = match;
	return Number(days ?? 0) * 86_400 + Number(hours ?? 0) * 3_600 + Number(minutes ?? 0) * 60 + Number(seconds ?? 0);
}

/**
 * Parse `ps axo pid=,ppid=,etime=,time=,rss=,command=` output.
 * The first five fields are fixed-position tokens; the rest is the command.
 */
export function parsePsOutput(output: string): ProcessRecord[] {
	const records: ProcessRecord[] = [];
	for (const line of output.split("\n")) {
		const trimmed = line.trim();
		if (!trimmed) continue;
		const match = /^(\d+)\s+(\d+)\s+(\S+)\s+(\S+)\s+(\d+)\s+(.*)$/.exec(trimmed);
		if (!match) continue;
		records.push({
			pid: Number(match[1]),
			ppid: Number(match[2]),
			elapsedSeconds: parseElapsed(match[3]),
			cpuSeconds: parseCpuTime(match[4]),
			rssKb: Number(match[5]),
			command: match[6],
		});
	}
	return records;
}

// ── Matching ────────────────────────────────────────────────────────────────

/**
 * Does this command line look like a running Elyra agent?
 * Matches the global bin (`elyra`), dist (`@elyracode/coding-agent/dist/cli.js`),
 * and from-source (`coding-agent/src/cli.ts`) forms, while excluding other
 * Elyra-branded apps (elyra-conductor, Elyra.app) and this very inspector.
 */
export function isElyraCommand(command: string): boolean {
	if (/@elyracode\/coding-agent\/dist\/cli\.js/.test(command)) return true;
	if (/coding-agent\/src\/cli\.ts/.test(command)) return true;
	// Bare bin invocation: "elyra", "elyra --continue", ".../bin/elyra ..."
	if (/(^|\/)elyra(\s|$)/.test(command)) return true;
	return false;
}

/** Human label for a known child process, or undefined for uninteresting ones. */
export function classifyChild(command: string): string | undefined {
	if (/typescript-language-server|tsserver/.test(command)) return "TypeScript language server";
	if (/intelephense/.test(command)) return "PHP language server (Intelephense)";
	if (/phpactor/.test(command)) return "PHP language server (phpactor)";
	if (/rust-analyzer/.test(command)) return "Rust language server (rust-analyzer)";
	if (/headless_shell|chromium|chrome/i.test(command)) return "Headless browser";
	if (/\belyra\b.*install/.test(command)) return "Package install";
	return undefined;
}

// ── Analysis ────────────────────────────────────────────────────────────────

export function findElyraInstances(records: ProcessRecord[], selfPid: number): ElyraInstance[] {
	const byPid = new Map(records.map((r) => [r.pid, r]));

	// Exclude the inspector's own instance subtree: walk self's ancestors and
	// find the topmost elyra-matching one (the bin wrapper, or the hosting
	// session when run via the bash tool inside Elyra), then drop that whole
	// subtree. The report deliberately shows *other* instances only.
	let excludedRoot: number | undefined;
	const seen = new Set<number>();
	let cursor = byPid.get(selfPid);
	while (cursor && !seen.has(cursor.pid)) {
		seen.add(cursor.pid);
		if (isElyraCommand(cursor.command)) excludedRoot = cursor.pid;
		cursor = byPid.get(cursor.ppid);
	}
	const excluded = new Set<number>([selfPid]);
	if (excludedRoot !== undefined) {
		excluded.add(excludedRoot);
		for (const descendant of collectDescendants(records, excludedRoot)) excluded.add(descendant.pid);
	}

	const matching = records.filter((r) => isElyraCommand(r.command));
	const matchingPids = new Set(matching.map((r) => r.pid));

	// Collapse parent/child pairs where both match (bin wrapper -> node):
	// keep the topmost of each matching chain.
	const roots = matching.filter((r) => !matchingPids.has(r.ppid) && !excluded.has(r.pid));

	return roots.map((root) => {
		const children = collectDescendants(records, root.pid);
		const totalRssKb = root.rssKb + children.reduce((sum, c) => sum + c.rssKb, 0);
		const warnings: string[] = [];
		if (totalRssKb > HIGH_MEMORY_KB) warnings.push("high memory");
		if (root.elapsedSeconds > LONG_RUNNING_SECONDS) warnings.push("running for more than a day");
		if (warnings.length === 2) warnings.push("likely forgotten - consider closing it");
		return { process: root, children, totalRssKb, warnings };
	});
}

function collectDescendants(records: ProcessRecord[], pid: number): ProcessRecord[] {
	const direct = records.filter((r) => r.ppid === pid);
	return [...direct, ...direct.flatMap((child) => collectDescendants(records, child.pid))];
}

// ── Formatting ──────────────────────────────────────────────────────────────

export function formatMb(kb: number): string {
	return `${Math.round(kb / 1024)} MB`;
}

export function formatDuration(seconds: number): string {
	if (seconds >= 86_400) return `${Math.floor(seconds / 86_400)}d ${Math.floor((seconds % 86_400) / 3_600)}h`;
	if (seconds >= 3_600) return `${Math.floor(seconds / 3_600)}h ${Math.floor((seconds % 3_600) / 60)}m`;
	if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
	return `${Math.floor(seconds)}s`;
}

export function formatResourceReport(instances: ElyraInstance[]): string {
	if (instances.length === 0) {
		return "No other Elyra instances are running.";
	}

	const lines: string[] = [];
	const totalKb = instances.reduce((sum, i) => sum + i.totalRssKb, 0);
	lines.push(
		`${instances.length} Elyra instance${instances.length === 1 ? "" : "s"} running, ${formatMb(totalKb)} total memory:`,
	);
	lines.push("");

	const sorted = [...instances].sort((a, b) => b.totalRssKb - a.totalRssKb);
	for (const instance of sorted) {
		const p = instance.process;
		const flags = instance.warnings.length > 0 ? `  [${instance.warnings.join(", ")}]` : "";
		lines.push(
			`- PID ${p.pid}: up ${formatDuration(p.elapsedSeconds)}, ` +
				`${formatDuration(p.cpuSeconds)} CPU time, ${formatMb(instance.totalRssKb)}${flags}`,
		);
		for (const child of instance.children) {
			const label = classifyChild(child.command);
			if (!label) continue;
			lines.push(`    child: ${label} (PID ${child.pid}, ${formatMb(child.rssKb)})`);
		}
	}

	const forgotten = sorted.filter((i) => i.warnings.length >= 2);
	if (forgotten.length > 0) {
		lines.push("");
		lines.push(
			`${forgotten.length} instance${forgotten.length === 1 ? " looks" : "s look"} forgotten. ` +
				`Close the terminal running ${forgotten.length === 1 ? "it" : "them"}, or: kill ${forgotten.map((i) => i.process.pid).join(" ")}`,
		);
	}

	lines.push("");
	lines.push("Idle Elyra uses no CPU; memory is held until an instance exits. Language servers");
	lines.push("and headless browsers listed above belong to their instance and exit with it.");

	return lines.join("\n");
}
